import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CalendarClock,
  Film,
  Image as ImageIcon,
  MessageSquare,
  Plus,
  Radio,
  Square,
} from 'lucide-react';
import { completeSession } from '@/lib/api';
import { formatDuration, formatNumber } from '@/lib/utils';
import type { LiveSession, ScheduleRule } from '@/types';
import { Button } from './ui/button';
import { SessionStatusBadge } from './SessionStatusBadge';

interface LiveFocusPanelProps {
  activeSession: LiveSession | null;
  nextSession: LiveSession | null;
  nextRule: ScheduleRule | null;
  onSessionUpdated: () => void;
  onCreateSession: () => void;
}

function formatDateTime(value?: string | null): string {
  if (!value) return '时间待定';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '时间待定';
  return date.toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCountdown(value?: string | null): string {
  if (!value) return '时间待定';
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return '时间待定';
  const minutes = Math.max(0, Math.floor((timestamp - Date.now()) / 60_000));
  if (minutes < 60) return minutes + ' 分钟';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours + ' 小时' + (rest ? ' ' + rest + ' 分' : '');
}

function getCurrentDuration(session: LiveSession): number {
  if (session.duration_seconds && session.duration_seconds > 0) return session.duration_seconds;
  if (!session.actual_start_time) return 0;
  const startedAt = new Date(session.actual_start_time).getTime();
  if (Number.isNaN(startedAt)) return 0;
  return Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
}

export default function LiveFocusPanel({
  activeSession,
  nextSession,
  nextRule,
  onSessionUpdated,
  onCreateSession,
}: LiveFocusPanelProps) {
  const navigate = useNavigate();
  const [isCompleting, setIsCompleting] = useState(false);

  const upcoming = useMemo(() => {
    if (nextSession) {
      return {
        title: nextSession.room_title || nextSession.streamer_name,
        subtitle: nextSession.streamer_name + ' · 房间 ' + nextSession.room_id,
        time: nextSession.scheduled_start_time,
        lead: null as number | null,
        action: () => navigate('/session/' + nextSession.id),
      };
    }

    if (nextRule) {
      return {
        title: nextRule.name || '房间 ' + nextRule.room_id,
        subtitle: '房间 ' + nextRule.room_id,
        time: nextRule.next_trigger_at,
        lead: nextRule.advance_minutes,
        action: () => navigate('/schedule-rules'),
      };
    }

    return null;
  }, [navigate, nextRule, nextSession]);

  const handleComplete = async () => {
    if (!activeSession || !window.confirm('确定要完成这个场次吗？完成后将无法继续录制。')) return;

    try {
      setIsCompleting(true);
      await completeSession(activeSession.id);
      onSessionUpdated();
    } catch (error) {
      console.error('完成场次失败:', error);
      window.alert('完成场次失败，请重试');
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <section aria-labelledby="live-focus-title">
      <h2 id="live-focus-title" className="sr-only">当前直播与下一场任务</h2>
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_372px]">
        <div className="console-panel">
          {activeSession ? (
            <div className="grid gap-0 md:grid-cols-[300px_minmax(0,1fr)]">
              <div className="relative min-h-[170px] overflow-hidden border-b border-[var(--structural-border)] bg-[repeating-linear-gradient(135deg,var(--surface-stripe-a)_0_10px,var(--surface-stripe-b)_10px_20px)] md:min-h-[252px] md:border-b-0 md:border-r">
                <div className="absolute inset-0 flex items-center justify-center">
                  <Radio className="h-12 w-12 text-[var(--border-strong)]" aria-hidden="true" />
                </div>
                {activeSession.cover_url && (
                  <img
                    src={activeSession.cover_url}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover"
                    referrerPolicy="no-referrer"
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
                  <SessionStatusBadge status="live" />
                  {activeSession.is_recording && (
                    <span className="rounded border border-[var(--status-live)] bg-[var(--surface-overlay)] px-2 py-1 text-[11px] font-semibold text-[var(--status-live)]">
                      REC · 录制中
                    </span>
                  )}
                </div>
              </div>

              <div className="flex min-w-0 flex-col p-4 md:p-[18px]">
                <p className="console-kicker">CURRENT SESSION · 当前直播</p>
                <h3 className="mt-2 line-clamp-2 text-lg font-bold leading-snug text-[var(--text-ink)] md:text-[19px]">
                  {activeSession.room_title || '未命名直播场次'}
                </h3>
                <p className="mt-2 text-xs text-[var(--text-muted)] md:text-[13px]">
                  {activeSession.streamer_name} · 房间号 {activeSession.room_id}
                  {activeSession.actual_start_time && (
                    <> · {formatDateTime(activeSession.actual_start_time)} 开播</>
                  )}
                </p>

                <dl className="mt-4 grid grid-cols-3 gap-2.5">
                  <div className="console-metric">
                    <dt className="console-metric-label flex items-center gap-1.5">
                      <MessageSquare className="h-3.5 w-3.5" />弹幕数
                    </dt>
                    <dd className="console-metric-value mt-1">{formatNumber(activeSession.danmaku_count)}</dd>
                  </div>
                  <div className="console-metric">
                    <dt className="console-metric-label flex items-center gap-1.5">
                      <ImageIcon className="h-3.5 w-3.5" />截图数
                    </dt>
                    <dd className="console-metric-value mt-1">{formatNumber(activeSession.screenshot_count)}</dd>
                  </div>
                  <div className="console-metric">
                    <dt className="console-metric-label flex items-center gap-1.5">
                      <Film className="h-3.5 w-3.5" />视频分段
                    </dt>
                    <dd className="console-metric-value mt-1">{formatNumber(activeSession.video_segment_count)}</dd>
                  </div>
                </dl>

                <p className="mt-3 font-mono text-[11px] text-[var(--text-muted)]">
                  已直播 {formatDuration(getCurrentDuration(activeSession))}
                </p>

                <div className="mt-auto grid grid-cols-2 gap-2 pt-4">
                  <Button type="button" onClick={() => navigate('/session/' + activeSession.id)}>
                    查看场次详情
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void handleComplete()}
                    disabled={isCompleting}
                  >
                    <Square className="h-3.5 w-3.5 fill-current" />
                    {isCompleting ? '正在完成' : '完成录制'}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex min-h-[252px] flex-col items-center justify-center px-5 py-9 text-center">
              <div className="flex h-11 w-11 items-center justify-center rounded-full border border-dashed border-[var(--border-control)] text-[var(--border-strong)]">
                <Radio className="h-5 w-5" />
              </div>
              <h3 className="mt-3 text-[15px] font-bold text-[var(--text-soft)]">当前没有进行中的直播</h3>
              <p className="mt-1.5 max-w-md text-xs leading-5 text-[var(--text-muted)]">
                采集服务处于待机状态，会在预定场次或预约规则触发时自动开始。
              </p>
              <Button type="button" onClick={onCreateSession} className="mt-4 min-h-11">
                <Plus className="h-4 w-4" />
                新建直播场次
              </Button>
            </div>
          )}
        </div>

        <aside className="console-panel flex min-h-[252px] flex-col p-[18px]">
          <div className="flex items-center justify-between">
            <p className="console-kicker">NEXT · 下一场任务</p>
            <CalendarClock className="h-4 w-4 text-[var(--status-action)]" aria-hidden="true" />
          </div>

          {upcoming ? (
            <>
              <h3 className="mt-3 line-clamp-2 text-base font-bold leading-snug text-[var(--text-ink)]">
                {upcoming.title}
              </h3>
              <p className="mt-1.5 font-mono text-xs text-[var(--text-muted)]">{upcoming.subtitle}</p>
              <dl className="mt-4 space-y-2.5 border-t border-[var(--border-subtle)] pt-3 text-xs">
                <div className="flex items-baseline justify-between gap-4">
                  <dt className="text-[var(--text-muted)]">计划开始</dt>
                  <dd className="font-mono text-[var(--text-ink)]">{formatDateTime(upcoming.time)}</dd>
                </div>
                {upcoming.lead !== null && (
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="text-[var(--text-muted)]">提前启动采集</dt>
                    <dd className="font-mono text-[var(--text-ink)]">{upcoming.lead} 分钟</dd>
                  </div>
                )}
                <div className="flex items-baseline justify-between gap-4">
                  <dt className="text-[var(--text-muted)]">距离开始</dt>
                  <dd className="font-mono text-[var(--action-blue)]">{formatCountdown(upcoming.time)}</dd>
                </div>
              </dl>
              <Button type="button" variant="outline" onClick={upcoming.action} className="mt-auto w-full">
                查看详情
                <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center py-6 text-center">
              <CalendarClock className="h-7 w-7 text-[var(--border-strong)]" />
              <p className="mt-3 text-sm font-medium text-[var(--text-soft)]">暂无下一场安排</p>
              <p className="mt-1 text-xs text-[var(--text-muted)]">可在预约规则中设置自动触发时间</p>
              <Button type="button" variant="outline" onClick={() => navigate('/schedule-rules')} className="mt-4">
                查看预约规则
              </Button>
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}
